import { X } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { countryLabel } from "@/lib/countries"
import { cn } from "@/lib/utils"
import type { CandidateFilters, CandidateStatus } from "@/types/candidates"

/**
 * OKS-17 — Active-filters chip strip rendered inside the No-Results
 * panel (UX Spec §No-results state). Each chip has an X to drop just
 * that filter; the parent's `onRemove` callback receives `(key, value?)`
 * so multi-value filters (status / tags) can be peeled one at a time.
 *
 * **OKS-19 additive — bulk-skipped chip.** When the parent passes
 * `bulkSkippedCount` (derived from `?bulk_skipped=…` URL param), an
 * additional removable chip ("Skipped from last bulk action (N)")
 * renders alongside the filter chips. Click-X dispatches
 * `onBulkSkippedRemove` (parent navigates to the URL minus the param).
 *
 * Locked testid: `candidate-list-bulk-skipped-chip` (per FE TRD §testid Map
 * — joint freeze with ux-oks19 + qa-oks19).
 */
interface ActiveFilterChipsProps {
  filters: CandidateFilters
  onRemove: (key: keyof CandidateFilters, value?: string) => void
  className?: string
  /**
   * **OKS-19** — when ≥1 candidate id is in the `?bulk_skipped=…` URL
   * param, the parent passes the count and a removal callback. When
   * `null` (param absent), no chip is rendered. Chip is removable;
   * removal navigates to the URL without `bulk_skipped`.
   */
  bulkSkippedCount?: number | null
  onBulkSkippedRemove?: () => void
}

export function ActiveFilterChips({
  filters,
  onRemove,
  className,
  bulkSkippedCount,
  onBulkSkippedRemove,
}: ActiveFilterChipsProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language.startsWith("fr") ? "fr" : "en"
  const chips: {
    key: keyof CandidateFilters
    label: string
    value?: string
    /**
     * OKS-953 — per-chip accessible name for the remove button.
     *
     * Every chip previously borrowed `tags_remove_aria`, so a screen-reader
     * user removing a rejection-reason chip heard "Remove TAG …". Optional
     * here rather than required: only the two OKS-953 families supply one, and
     * the shipped families keep their existing string unchanged. Widening the
     * fix to status/country/city chips is a11y work in OKS-954's lane, not a
     * silent expansion of this ticket — but it IS the same defect and the
     * fixture is the same one line.
     */
    removeAria?: string
  }[] = []

  if (filters.q) {
    chips.push({
      key: "q",
      label: `"${filters.q}"`,
    })
  }
  filters.status.forEach((s) => {
    chips.push({
      key: "status",
      value: s,
      label: statusLabel(t, s),
    })
  })
  if (filters.followers_min !== null || filters.followers_max !== null) {
    chips.push({
      key: "followers_min",
      label: `${t("candidates.list.filters.followers_label")}: ${formatRange(
        filters.followers_min,
        filters.followers_max,
      )}`,
    })
  }
  // Country chip surfaces in two distinct states (CTPO V1 lock):
  //   - filters.country: "France" (or any non-empty value) → label
  //     "Country: France"; remove → setFilter("country", "") (explicit
  //     opt-out of default-France).
  //   - filters.country: "" (explicit cleared) → no chip rendered;
  //     the `null` (default) state also doesn't render.
  if (filters.country && filters.country !== "") {
    // OKS-348 — chip label is the LOCALIZED country name (e.g.
    // "Pays : France"); URL/wire stays alpha-2 (`?country=FR`).
    chips.push({
      key: "country",
      label: `${t("candidates.list.filters.country_label")}: ${countryLabel(filters.country, locale)}`,
    })
  }
  if (filters.city) {
    chips.push({
      key: "city",
      label: `${t("candidates.list.filters.city_label")}: ${filters.city}`,
    })
  }
  filters.tags.forEach((tag) => {
    chips.push({
      key: "tags",
      value: tag,
      label: `#${tag}`,
    })
  })
  // OKS-953 — both new filters are multi-select, so each value peels off
  // individually via the existing `(key, value)` removal contract.
  filters.reason_code.forEach((code) => {
    chips.push({
      key: "reason_code",
      value: code,
      label: `${t("candidates.list.filters.reason_code_label")}: ${t(`candidates.reason_code.${code}`)}`,
      removeAria: t("candidates.list.filters.reason_code_remove_aria", {
        label: t(`candidates.reason_code.${code}`),
      }),
    })
  })
  filters.contact_channel.forEach((channel) => {
    chips.push({
      key: "contact_channel",
      value: channel,
      label: `${t("candidates.list.filters.contact_channel_label")}: ${t(`candidates.contact.channel.options.${channel}`)}`,
      removeAria: t("candidates.list.filters.contact_channel_remove_aria", {
        label: t(`candidates.contact.channel.options.${channel}`),
      }),
    })
  })

  // OKS-19 — bulk-skipped chip is renderable independently of filter
  // chips (only condition is `bulkSkippedCount` truthy). When BOTH
  // bulk-skipped and filters are absent, return null. Otherwise the
  // chip rail renders with whatever subset is active.
  const hasBulkSkipped =
    typeof bulkSkippedCount === "number" && bulkSkippedCount > 0
  if (chips.length === 0 && !hasBulkSkipped) return null

  return (
    <ul
      role="list"
      aria-label={t("candidates.list.no_results.active_filters_aria")}
      className={cn("flex flex-wrap gap-2", className)}
    >
      {chips.map((chip, idx) => (
        <li key={`${String(chip.key)}:${chip.value ?? idx}`}>
          <Badge variant="secondary" className="gap-1.5 pr-1">
            <span>{chip.label}</span>
            <button
              type="button"
              onClick={() => onRemove(chip.key, chip.value)}
              aria-label={
                chip.removeAria ??
                t("candidates.list.filters.tags_remove_aria", {
                  tag: chip.label,
                })
              }
              className="hover:bg-muted -mr-0.5 rounded-sm p-0.5"
            >
              <X className="size-3" aria-hidden="true" />
            </button>
          </Badge>
        </li>
      ))}
      {hasBulkSkipped && (
        <li>
          <Badge
            variant="secondary"
            className="gap-1.5 pr-1"
            data-testid="candidate-list-bulk-skipped-chip"
          >
            <span>
              {t("candidates.list.bulk.skipped_chip_label", {
                count: bulkSkippedCount,
              })}
            </span>
            <button
              type="button"
              onClick={() => onBulkSkippedRemove?.()}
              aria-label={t("candidates.list.bulk.skipped_chip_remove_aria")}
              className="hover:bg-muted -mr-0.5 rounded-sm p-0.5"
            >
              <X className="size-3" aria-hidden="true" />
            </button>
          </Badge>
        </li>
      )}
    </ul>
  )
}

function statusLabel(
  t: ReturnType<typeof useTranslation>["t"],
  status: CandidateStatus,
): string {
  switch (status) {
    case "new":
      return t("candidates.status.new")
    case "reviewed":
      return t("candidates.status.reviewed")
    case "pending_brand_validation":
      return t("candidates.status.pending_brand_validation")
    case "approved":
      return t("candidates.status.approved")
    case "contacted":
      return t("candidates.status.contacted")
    case "invited":
      return t("candidates.status.invited")
    case "joined":
      return t("candidates.status.joined")
    case "rejected":
      return t("candidates.status.rejected")
    case "blocklisted":
      return t("candidates.status.blocklisted")
  }
}

function formatRange(min: number | null, max: number | null): string {
  if (min !== null && max !== null) return `${min}–${max}`
  if (min !== null) return `≥ ${min}`
  if (max !== null) return `≤ ${max}`
  return ""
}
